import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api_middleware";
import { RootState } from "../store";
import {
  SessionFilters,
  filtersToApiFormat,
} from "../../components/dashboard/sessions/types";

export interface FetchSessionsParams {
  limit?: number;
  page_number?: number;
  cursor?: string;
  reset?: boolean;
  include_test_infra?: boolean;
}

export interface FetchSessionsV2Params {
  filters?: SessionFilters;
  limit?: number;
  page_number?: number;
  cursor?: string;
  reset?: boolean;
}

export const isoNow = () => {
  const d = new Date();
  return (
    d.toISOString().replace("Z", "") +
    d.getUTCMilliseconds().toString().padStart(3, "0") +
    "Z"
  );
};

export const fetchSessions = createAsyncThunk(
  "sessions/fetchSessions",
  async (params: FetchSessionsParams = {}, { getState }) => {
    const state = getState() as RootState;
    const now = Date.now();

    // Cache check (25 minutes)
    const TWENTY_FIVE_MINUTES = 25 * 60 * 1000;
    if (
      !params.reset &&
      now - state.sessions.lastFetchTime < TWENTY_FIVE_MINUTES &&
      state.sessions.sessions.length > 0 &&
      params.page_number === undefined
    ) {
      return {
        data: state.sessions.sessions,
        fromCache: true,
        page_number: state.sessions.page_number,
        has_more_entries: state.sessions.hasMoreEntries,
        cursor: state.sessions.initialCursor,
      };
    }

    // Determine cursor
    // When navigating pages (page > 0), use the initial cursor to stay in the same time window
    // When starting fresh (page 0 without explicit cursor), use current time
    const effectiveCursor =
      params.cursor ??
      (params.page_number && params.page_number > 0
        ? state.sessions.initialCursor || isoNow()
        : isoNow());

    // Build request body
    const body = {
      limit: params.limit ?? 100,
      page_number: params.page_number ?? 0,
      cursor: effectiveCursor,
      include_test_infra: params.include_test_infra ?? true,
    };

    const res = await api(
      `v1/admin/pixels/sessions/list`,
      "POST",
      body
    );

    return {
      data: res.items,
      fromCache: false,
      page_number: res.page_number ?? 0,
      has_more_entries: res.has_more_entries,
      next_cursor: res.next_cursor,
      cursor: effectiveCursor,
    };
  }
);

// End (terminate) a session
export const endSession = createAsyncThunk(
  "sessions/endSession",
  async (sessionId: string) => {
    const response = await api(
      `sessions/end-session`,
      "POST",
      { session_id: sessionId },
      { usePixelsApi: true }
    );
    return response;
  }
);

/**
 * Fetch sessions using the V2 API with enhanced filtering and enrichment.
 */
export const fetchSessionsV2 = createAsyncThunk(
  "sessions/fetchSessionsV2",
  async (params: FetchSessionsV2Params = {}, { getState }) => {
    const state = getState() as RootState;
    const now = Date.now();

    // Cache check (25 minutes) - only if no filters active and not resetting
    const TWENTY_FIVE_MINUTES = 25 * 60 * 1000;
    const hasFilters = params.filters && Object.keys(params.filters).length > 0;

    if (
      !params.reset &&
      !hasFilters &&
      now - state.sessions.lastFetchTime < TWENTY_FIVE_MINUTES &&
      state.sessions.sessions.length > 0 &&
      params.page_number === undefined
    ) {
      return {
        data: state.sessions.sessions,
        fromCache: true,
        page_number: state.sessions.page_number,
        has_more_entries: state.sessions.hasMoreEntries,
        cursor: state.sessions.initialCursor,
        filters: params.filters,
      };
    }

    // Determine cursor
    const effectiveCursor =
      params.cursor ??
      (params.page_number && params.page_number > 0
        ? state.sessions.initialCursor || isoNow()
        : isoNow());

    // Build request body
    const body = {
      filters: params.filters ? filtersToApiFormat(params.filters) : undefined,
      limit: params.limit ?? 100,
      page_number: params.page_number ?? 0,
      cursor: effectiveCursor,
    };

    const res = await api(`v1/admin/pixels/sessions/list`, "POST", body);

    return {
      data: res.items,
      fromCache: false,
      page_number: res.page_number ?? 0,
      has_more_entries: res.has_more_entries,
      next_cursor: res.next_cursor,
      cursor: effectiveCursor,
      filters: params.filters,
      total_count: res.total_count,
    };
  }
);